import { useState } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PrintIcon from '@mui/icons-material/Print';
import type { BracketNode as BracketNodeData } from '../domain';
import {
  buildBracketScoreSheet,
  buildNodeRefereeForm,
  gamesToWin,
  isBracketMatchPrintable,
  summarizeFootballMatch,
  summarizeMatch,
} from '../domain';
import { useTournament } from '../state/TournamentContext';
import { useTranslation } from '../state/I18nContext';
import { printBracketScoreSheets, printRefereeForms } from './printScoreSheet';
import { describeRef, playerName } from './labels';
import { GamesEditor, useGamesDraft } from './GamesEditor';
import { FootballMatchDialog } from './FootballScoreEditor';

const NODE_WIDTH = 210;

interface BracketNodeProps {
  nodeId: string;
}

export function BracketNode({ nodeId }: BracketNodeProps) {
  const { tournament } = useTournament();
  const { t, locale } = useTranslation();
  const node = tournament?.nodes[nodeId];
  const [open, setOpen] = useState(false);

  if (!node || !tournament) return null;

  const isFootball = tournament.sportId === 'FOOTBALL';

  // Both participants resolved: the match can be scored and its sheet printed.
  const ready = isBracketMatchPrintable(node);
  const done = node.status === 'COMPLETED';
  const summary = summarizeMatch(node.games, node.bestOf);
  const football = summarizeFootballMatch(node.games);

  // Football shows goals (with the shoot-out in brackets); table tennis shows
  // games won in the series.
  const scoreA = isFootball
    ? football.hasShootout
      ? t('admin.football.scoreWithPens', { goals: football.goalsA, pens: football.shootoutA })
      : football.goalsA
    : summary.gamesA;
  const scoreB = isFootball
    ? football.hasShootout
      ? t('admin.football.scoreWithPens', { goals: football.goalsB, pens: football.shootoutB })
      : football.goalsB
    : summary.gamesB;

  const nameA = node.playerA
    ? playerName(tournament, node.playerA)
    : describeRef(node.a, tournament, t);
  const nameB = node.playerB
    ? playerName(tournament, node.playerB)
    : describeRef(node.b, tournament, t);

  const handlePrint = () => {
    // Football prints the referee's match-report form; table tennis the
    // fill-in score sheet.
    if (isFootball) {
      printRefereeForms([buildNodeRefereeForm(tournament, node.id)], locale);
    } else {
      printBracketScoreSheets([buildBracketScoreSheet(tournament, node.id)], locale);
    }
  };

  return (
    <>
      <Paper
        variant="outlined"
        sx={{
          width: NODE_WIDTH,
          overflow: 'hidden',
          borderColor: done ? 'primary.main' : 'divider',
        }}
      >
        {/* The header sits outside the ButtonBase so its print action is not a
            nested button; the participant rows stay the click-to-edit area. */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            pl: 1,
            pr: 0.5,
            minHeight: 24,
            bgcolor: 'action.hover',
          }}
        >
          <Typography
            variant="caption"
            noWrap
            sx={{ flexGrow: 1, color: 'text.secondary', fontWeight: 700 }}
          >
            {node.label}
          </Typography>
          {ready && (
            <Tooltip
              title={
                isFootball
                  ? t('admin.bracket.printRefereeForm', { label: node.label })
                  : t('admin.bracket.printSheet', { label: node.label })
              }
              arrow
            >
              <IconButton
                size="small"
                aria-label={
                  isFootball
                    ? t('admin.bracket.printRefereeForm', { label: node.label })
                    : t('admin.bracket.printSheet', { label: node.label })
                }
                onClick={handlePrint}
                sx={{ p: 0.25 }}
              >
                <PrintIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {/* The header text is outside the button, so name the match here too;
            the whole card used to be the button and announced its label. */}
        <ButtonBase
          disabled={!ready}
          onClick={() => setOpen(true)}
          aria-label={t('admin.bracket.editResult', { label: node.label, nameA, nameB })}
          sx={{ display: 'block', width: '100%', textAlign: 'left' }}
        >
          <ParticipantRow
            name={nameA}
            resolved={node.playerA !== null}
            score={done ? scoreA : null}
            winner={done && node.winner === node.playerA}
          />
          <Box sx={{ height: '1px', bgcolor: 'divider' }} />
          <ParticipantRow
            name={nameB}
            resolved={node.playerB !== null}
            score={done ? scoreB : null}
            winner={done && node.winner === node.playerB}
          />
        </ButtonBase>
      </Paper>

      <NodeEditor node={node} open={open} onClose={() => setOpen(false)} />
    </>
  );
}

interface ParticipantRowProps {
  name: string;
  resolved: boolean;
  score: number | string | null;
  winner: boolean;
}

function ParticipantRow({ name, resolved, score, winner }: ParticipantRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.75,
        bgcolor: winner ? 'action.selected' : 'transparent',
      }}
    >
      {winner && (
        <EmojiEventsIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
      )}
      <Typography
        variant="body2"
        noWrap
        sx={{
          flexGrow: 1,
          fontWeight: winner ? 700 : 400,
          fontStyle: resolved ? 'normal' : 'italic',
          color: resolved ? 'text.primary' : 'text.secondary',
        }}
      >
        {name}
      </Typography>
      {score !== null && (
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {score}
        </Typography>
      )}
    </Box>
  );
}

interface NodeEditorProps {
  node: BracketNodeData;
  open: boolean;
  onClose: () => void;
}

function NodeEditor({ node, open, onClose }: NodeEditorProps) {
  const { tournament } = useTournament();
  if (tournament?.sportId === 'FOOTBALL') {
    return <FootballNodeEditor node={node} open={open} onClose={onClose} />;
  }
  return <BestOfNodeEditor node={node} open={open} onClose={onClose} />;
}

function FootballNodeEditor({ node, open, onClose }: NodeEditorProps) {
  const { tournament } = useTournament();
  const { t } = useTranslation();
  if (!tournament) return null;

  return (
    <FootballMatchDialog
      open={open}
      onClose={onClose}
      title={node.label}
      subtitle={t('admin.football.bracketSubtitle', { id: node.id })}
      matchId={node.id}
      games={node.games}
      events={node.events}
      phase="bracket"
      refereeId={node.refereeId}
      nameA={playerName(tournament, node.playerA)}
      nameB={playerName(tournament, node.playerB)}
      membersA={
        (node.playerA ? tournament.players[node.playerA]?.members : []) ?? []
      }
      membersB={
        (node.playerB ? tournament.players[node.playerB]?.members : []) ?? []
      }
    />
  );
}

function BestOfNodeEditor({ node, open, onClose }: NodeEditorProps) {
  const { tournament, updateNode } = useTournament();
  const { t } = useTranslation();
  const draft = useGamesDraft(open, node.games, node.bestOf);

  if (!tournament) return null;

  const nameA = playerName(tournament, node.playerA);
  const nameB = playerName(tournament, node.playerB);

  const save = () => {
    if (!draft.validation.valid) return;
    void updateNode(node.id, draft.cleaned);
    onClose();
  };

  const clear = () => {
    void updateNode(node.id, []);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{node.label}</DialogTitle>
      <DialogContent>
        <Typography variant="caption" color="text.secondary">
          {t('admin.bracket.editor.meta', {
            id: node.id,
            bestOf: node.bestOf,
            gamesToWin: gamesToWin(node.bestOf),
          })}
        </Typography>

        <GamesEditor draft={draft} bestOf={node.bestOf} nameA={nameA} nameB={nameB} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={clear}>
          {t('admin.bracket.editor.clear')}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose}>{t('admin.common.cancel')}</Button>
        <Button variant="contained" disabled={!draft.validation.valid} onClick={save}>
          {t('admin.bracket.editor.saveResult')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
